import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  Star,
  Speed,
  Assignment,
  Visibility,
  FilterList,
  CheckCircle,
  Email,
  Phone,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';
import ProviderBidDetailsDialog from '../components/ProviderBidDetailsDialog';

interface PerformanceData {
  overview: {
    totalJobs: number;
    totalEarnings: number;
    totalPlatformFees: number;
    avgRating: number;
    totalReviews: number;
    bidAcceptanceRate: number;
    avgResponseTimeHours: number;
  };
  ratings?: {
    overall: {
      average: number;
      total: number;
    };
  };
  earnings: {
    total: number;
    fromStandardFeeCustomers: number;
    fromImportedCustomers: number;
    breakdown: {
      standardFeeJobs: number;
      importedFeeJobs: number;
    };
  };
  savings: {
    importedCustomersCount: number;
    importedCustomerJobs: number;
    totalFeeSavings: number;
    savingsPerJob: number;
    effectiveFeeRate: number;
  };
  activity: {
    totalBids: number;
    acceptedBids: number;
    pendingBids: number;
    rejectedBids: number;
  };
  insurance: {
    hasInsurance: boolean;
    expiryDate: string;
    daysUntilExpiry: number;
    isExpired: boolean;
    verified: boolean;
  };
  allBids: Array<{
    id: string;
    price: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    platformFeePercentage?: number;
    serviceType: any;
    servicePricing: Array<{
      service: string;
      serviceName: string;
      price: number;
      duration: number;
      frequency: string;
      isSeasonalContract: boolean;
      seasonalContractDetails?: any;
    }>;
    requestId?: string;
    isAdditionalServices?: boolean;
    serviceRequestStatus?: string | null;
    providerEstimateAttachment?: {
      name: string;
      mimeType?: string;
      uploadedAt?: string;
      viewUrl?: string | null;
      missing?: boolean;
    } | null;
    requestData?: {
      address: any;
      propertyType: string;
      description: string;
      customerId: any;
    };
  }>;
  reviews?: Array<{
    id: string;
    rating: number;
    title?: string;
    comment: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    serviceType: string;
    completedDate?: string;
    createdAt: string;
  }>;
}

const ProviderPerformancePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bidFilter, setBidFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected' | 'deleted'>('all');
  const [bidDetailsId, setBidDetailsId] = useState<string | null>(null);
  const [bidDetailsDialog, setBidDetailsDialog] = useState(false);

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  // Open Bid Details from ?bidId= (Dashboard My Bids and deep links)
  useEffect(() => {
    const bidId = searchParams.get('bidId');
    if (bidId) {
      setBidDetailsId(bidId);
      setBidDetailsDialog(true);
    }
  }, [searchParams]);

  const fetchPerformanceData = async (opts?: { quiet?: boolean }) => {
    try {
      if (!opts?.quiet) setLoading(true);
      const response = await apiService.get('/provider-performance');
      if (response.success) {
        setData(response.data as any);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reviews and bids');
    } finally {
      if (!opts?.quiet) setLoading(false);
    }
  };

  const openBidDetails = (bidId: string) => {
    setBidDetailsId(bidId);
    setBidDetailsDialog(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('bidId', bidId);
      return next;
    }, { replace: true });
  };

  const closeBidDetails = () => {
    setBidDetailsDialog(false);
    setBidDetailsId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('bidId');
      return next;
    }, { replace: true });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading reviews and bids...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Reviews & Bids
      </Typography>

      {/* Customer Reviews */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Star sx={{ color: '#FFD700', mr: 1 }} />
          <Typography variant="h6">Customer Reviews</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* Overall Rating and Total Jobs Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Overall Average Rating
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Star sx={{ color: '#FFD700', fontSize: 32 }} />
                  <Typography variant="h3" fontWeight="bold">
                    {data.ratings?.overall?.average?.toFixed(1) || data.overview.avgRating.toFixed(1)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  From {data.ratings?.overall?.total || data.overview.totalReviews} review{(data.ratings?.overall?.total || data.overview.totalReviews) !== 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Jobs Completed
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ color: '#4CAF50', fontSize: 32 }} />
                  <Typography variant="h3" fontWeight="bold">
                    {data.overview.totalJobs || 0}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  All-time completed jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {data.reviews && data.reviews.length > 0 ? (
          <List sx={{ mt: 2 }}>
            {data.reviews.map((review) => (
              <React.Fragment key={review.id}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    bgcolor: review.rating <= 2 ? 'error.light' : review.rating === 3 ? 'warning.light' : 'success.light',
                    borderRadius: 2,
                    mb: 2,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {review.customerName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            sx={{
                              color: i < review.rating ? '#FFD700' : 'grey.300',
                              fontSize: 18,
                            }}
                          />
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {review.serviceType} • {review.completedDate ? formatDateForDisplay(review.completedDate) : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  {review.title && (
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
                      {review.title}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {review.comment}
                  </Typography>

                  {/* Customer Contact Information */}
                  <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mt: 1 }}>
                    <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 1 }}>
                      Contact Customer:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {review.customerEmail && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2" component="a" href={`mailto:${review.customerEmail}`} sx={{ textDecoration: 'none', color: 'primary.main' }}>
                            {review.customerEmail}
                          </Typography>
                        </Box>
                      )}
                      {review.customerPhone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2" component="a" href={`tel:${review.customerPhone}`} sx={{ textDecoration: 'none', color: 'primary.main' }}>
                            {review.customerPhone}
                          </Typography>
                        </Box>
                      )}
                      {!review.customerEmail && !review.customerPhone && (
                        <Typography variant="body2" color="text.secondary">
                          Contact information not available
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No customer reviews yet. Complete jobs and encourage customers to leave feedback.
          </Typography>
        )}
      </Paper>

      {/* Bid Performance */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Assignment sx={{ mr: 1 }} />
          <Typography variant="h6">Bid Performance</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Bid Acceptance Rate</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.overview.bidAcceptanceRate.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={data.overview.bidAcceptanceRate}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Speed fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">Average Response Time</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {data.overview.avgResponseTimeHours}h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Time to submit bid after request
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`${data.activity.totalBids} total bids`} size="small" />
              <Chip label={`${data.activity.acceptedBids} accepted`} size="small" color="success" />
              <Chip label={`${data.activity.pendingBids} open`} size="small" color="warning" />
              <Chip label={`${data.activity.rejectedBids} rejected`} size="small" color="error" />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            All Bids
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList fontSize="small" color="action" />
            <Tabs
              value={bidFilter}
              onChange={(e, newValue) => setBidFilter(newValue)}
              sx={{ minHeight: 'auto' }}
            >
              <Tab label="All" value="all" />
              <Tab label="Open" value="pending" />
              <Tab label="Accepted" value="accepted" />
              <Tab label="Rejected" value="rejected" />
              <Tab label="Deleted" value="deleted" />
            </Tabs>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {!data.allBids || data.allBids.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            No bids found
          </Typography>
        ) : (
          <List>
            {data.allBids
              .filter(bid => {
                if (bidFilter === 'all') return true;
                if (bidFilter === 'pending') return bid.status === 'pending';
                if (bidFilter === 'accepted') return bid.status === 'accepted';
                if (bidFilter === 'rejected') return bid.status === 'rejected';
                if (bidFilter === 'deleted') return bid.status === 'deleted';
                return true;
              })
              .map((bid, index, filteredBids) => (
                <React.Fragment key={bid.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {bid.servicePricing && bid.servicePricing.length > 0
                                ? bid.servicePricing.map(sp => sp.serviceName).join(', ')
                                : bid.serviceType?.name || 'Service Request'}
                            </Typography>
                            <Chip
                              label={bid.status.charAt(0).toUpperCase() + bid.status.slice(1)}
                              size="small"
                              color={
                                bid.status === 'accepted'
                                  ? 'success'
                                  : bid.status === 'pending'
                                  ? 'warning'
                                  : bid.status === 'rejected'
                                  ? 'error'
                                  : 'default'
                              }
                            />
                          </Box>
                          {bid.requestData?.address && (
                            <Typography variant="body2" color="text.secondary">
                              {bid.requestData.address.street}, {bid.requestData.address.city}, {bid.requestData.address.state} {bid.requestData.address.zipCode}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Bid Amount: {formatCurrency(bid.price)} • Submitted: {new Date(bid.createdAt).toLocaleDateString()}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => openBidDetails(String(bid.id))}
                            title="View Bid Details"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < filteredBids.length - 1 && <Divider />}
                </React.Fragment>
              ))}
          </List>
        )}
      </Paper>

      <ProviderBidDetailsDialog
        open={bidDetailsDialog}
        bidId={bidDetailsId}
        onClose={closeBidDetails}
        onUpdated={() => fetchPerformanceData({ quiet: true })}
      />
    </Container>
  );
};

export default ProviderPerformancePage;

